import type { Option } from "@tento-lona/sheets";
import type { ListenerErrorSink } from "@tento-core/listener-errors";
import { NaiveDate, DateTime, Utc } from "@tento-chrono";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import { nonnull } from "@tento-core/assert";
import type { EventsTable } from "../indices/events-table.ts";
import { liveRowOf } from "@tento-lona/sheets/internal-ui";
import { RowTypeSpecOps } from "@tento-lona/sheets/platform";
import { TypedApi } from "@tento-core/typed-api";
import type { Rational } from "@tento-core/rational";
import { CellId, RowLocalId, UserId } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import { RowType, RowTypeDescriptor, CellProvider } from "./row-descriptor.ts";
import { SheetRegistries } from "@tento-lona/sheets";
import type { Capabilities, CellLayoutOptions, HeaderSecondRowKind, LayoutSpec, Timescale } from "@tento-lona/sheets";
import { RowKey } from "@tento-lona/sheets";
import { MoveWrites, type RowFacade } from "@tento-lona/sheets/internal-sdk";
import { RowFieldWrites } from "./row-field-writes.ts";
import { CellHandle } from "../cells/cell-handle.ts";
import { RowRangeCellHandle } from "../cells/range-cell-handle.ts";
import type { CellValue } from "@tento-lona/sheets";
import { CellTarget } from "../cells/cell-target.ts";
import type { SheetHost } from "../client/sheet-host.ts";
import type { SheetRowEdge } from "./sheet-row-edge.ts";
import type { RowHeightPreferences } from "../preferences/row-height-preferences.ts";
import { Actor, type InvalidationSink } from "../invalidation.ts";
import type { DependencyGraph } from "../formula/dependency-graph.ts";
import { Tracing } from "@tento-core/tracing";
import { RowWriteError } from "../errors.ts";
import type { Durable, DurableHandoffs } from "../client/durable-handoffs.ts";
import { RowCellCache } from "../sync/row-cell-cache.ts";
import { RowId } from "@tento-lona/sheets";
import { RowPatch } from "@tento-lona/sheets";
import { RowNetworkKey } from "@tento-lona/sheets";
import type { Sheet } from "@tento-lona/sheets/platform";
import type { ModelRow } from "@tento-lona/sheets";
import { Json, type JsonObject } from "@tento-lona/sheets";
import type { ChildSpec, Graph } from "@tento-lona/sheets";
import { SheetId } from "@tento-lona/sheets";
import * as handleModule from "./sheet-row-handle.ts";

export {
  beginFormulaRewrite,
  leasedRowOf,
  leaseTargetOf,
  peekRowHandleOf,
  placeInTree,
  ROW_HANDLE,
  rowHandleOf,
  sheetRowOf,
  wireSheetRowHandle,
} from "./sheet-row-handle.ts";
export type { HasRowHandle, RowFacadePort } from "./sheet-row-handle.ts";
export { type FormulaRewrite, rereadFields } from "./sheet-row-updates.ts";
export { heldDateCellOf, heldTimepointCellOf } from "./sheet-row-cells.ts";

/**
 * A row PATCH body as the SDK sends and tracks it
 * (`Network.PatchRow_PendingValidation`): the public `RowPatch` encoded
 * (`RowPatch.encode`), or a verb's own body (`setLookupKey`,
 * `mergeAttributes`). Every update's pending model works on this shape.
 */
type WirePatch = Network.PatchRow_PendingValidation;

/**
 * The door onto a `SheetRow`'s descriptor, assigned by the class's static
 * block: the registry (`SheetRow.Registry.set`, resolving through the
 * client's registries) and the render layer's overrides
 * (`SheetRow.DescriptorOverrides.apply`) replace it; nothing else can.
 */
let replaceDescriptor: (row: SheetRow, descriptor: RowTypeDescriptor) => void;

/**
 * The door onto a `SheetRow`'s timepoint-cell moves in flight — the one move
 * ledger (`MoveWrites`, keyed by cell id) of a row whose cells the SDK's own
 * cache holds — assigned by the class's static block: made on the backing
 * row's first move and shared by every facade over it (each standalone
 * `client.rows.open` facade), so overlapping moves of one cell settle as one
 * facade's do.
 */
let movesInFlightOf: (row: SheetRow) => MoveWrites<string, DateTime.Range<Utc>>;

/**
 * **SDK-internal** (not on any barrel): what every `SheetRow.Handle` is
 * built with — none of it optional. The client's API, host and sink; its
 * lifecycle (every network verb of the row checks it first, so a row held
 * past `client.dispose()` refuses with `LonaSdk.Client.DisposedError`); its
 * durable writes in flight (through which a refused `updateDurable` /
 * `writeDurable` rejects its `settled` — never "settled once handed off");
 * and its row-handle resolver (the client-scoped backend registry, so every
 * consumer of the row converges on ONE `RowCellCache`).
 */
export type SheetRowHandleDeps = {
  readonly api: TypedApi<Network.Routes>;
  readonly host: SheetHost;
  readonly invalidation: InvalidationSink;
  readonly lifecycle: ClientLifecycle;
  readonly durableWrites: DurableHandoffs;
  readonly handleResolver: (rowId: RowId) => RowCellCache;
  /** Propagates a held row's rename, and recovers a promote that may have
   *  stored (`RowMirror`). */
  readonly mirror: RowMirror;
  /** The client's row-field ledger and its one transport (`RowFieldWrites`):
   *  every write of the row's own fields enters it. */
  readonly writes: RowFieldWrites;
  /** Where a throw of one of the row's subscribers goes (the client's
   *  `listenerErrorHandler`); absent for a row no client built, which
   *  rethrows (`ListenerErrors.RETHROW`). */
  readonly listenerErrors?: ListenerErrorSink;
  /** Whether a promote sends the draft's client-minted row id (row-identity
   *  stage 2, `PendingRowPersist`): read at each promote. Absent: off. */
  readonly clientRowIds?: () => boolean;
};

/**
 * **SDK-internal**: what a held row's write asks of the client beyond the
 * row-field ledger (`RowWrites`, which shows every write in every copy of
 * the row itself). A rename (`renamed`, announced before the new label is
 * written) rewrites the formulas that name the row by its old label — the
 * one owner of rename propagation — and answers the rewrites' settle
 * (`RowMirror.Renamed`). After a promote that may have stored
 * (`mayHaveCreated`), every open handle of the sheet the promote posted to
 * re-reads its row list and adopts a top-level row of the draft's type (and
 * lookup key) that the server lists and it does not; it answers `null`.
 */
export type RowMirror = (id: RowLocalId, change: RowMirror.Change) => Option<Promise<void>>;

/** Types and values that go with `RowMirror`: `Change`, `MayHaveCreated`, `NONE`. */
export namespace RowMirror {
  /** One change: a rename to propagate, or a row a promote may have
   *  created in `sheet` (see `RowMirror`). */
  export type Change =
    | { readonly mayHaveCreated: MayHaveCreated }
    | { readonly renamed: Renamed };

  /**
   * A rename of the row from label `from` to `to`, announced before the new
   * label is written: the formulas that name the row as `rows["from"]` are
   * rewritten to `rows["to"]` at once — each a write of its own row entered
   * in the row-field ledger (`beginFormulaRewrite`), painted as `actor`, the
   * label and its rewrites one write group (`RowWrites.group`) — and sent
   * once `landed` (the label write's verdict) resolves, the checked or
   * durable way `mode` names. When `landed` rejects (the label was refused or
   * went unanswered) the group is taken back together: every rewrite, and
   * nothing of them sent. The mirror answers a promise that settles once
   * every rewrite did; it rejects with the first refused rewrite's
   * `RowWriteError("update")` — a rewrite refused AFTER the label landed is
   * taken back alone (its row re-read), and the label stands.
   */
  export type Renamed = {
    readonly from: string;
    readonly to: string;
    readonly landed: Promise<void>;
    readonly mode: "checked" | "durable";
    readonly actor: Actor;
  };

  /** A row a promote into `sheet` may have created: its wire `type` and
   *  lookup key (`null`: none). */
  export type MayHaveCreated = {
    readonly sheet: SheetId;
    readonly type: string;
    readonly lookupKey: Option<RowNetworkKey>;
  };

  /** A mirror that reaches nothing (a row no client registry holds). */
  export const NONE: RowMirror = (): Option<Promise<void>> => null;
}

/**
 * **SDK-internal**: the standalone held row over `inner`, a row of the
 * caller's registry (`SheetRow.Registry`): `client.rows.open`. A row of a
 * sheet is the sheet's interned facade (`rowFacadeOf`).
 */
export function sheetRowHandleOf(inner: SheetRow, deps: SheetRowHandleDeps): SheetRow.Handle {
  return handleModule.standaloneFacadeOf(inner, deps, FACADE_PORT);
}

/**
 * **SDK-internal**: the held row for `raw` (a row the server answered),
 * interned in `registry` — the caller's identity scope, so its `RowLocalId`
 * is the one every other consumer of the client holds for that key.
 */
export function sheetRowHandleFromWire(
  raw: Network.SheetRowDb,
  deps: SheetRowHandleDeps,
  registry: RowLocalId.Registry,
): SheetRow.Handle {
  const localId = registry.resolve(RowKey.of(raw.id.toString()));
  return handleModule.standaloneFacadeOf(SheetRow.from(localId, raw as SheetRow.Input, null), deps, FACADE_PORT);
}

/**
 * **SDK-internal** (`client.rows.pending`): a pending row of wire type
 * `type` — local only, its updates buffered until `promote` persists it —
 * interned in `registry`, standalone (no sheet cell writer, parent or
 * rename hook). The held row `promote` resolves is the draft's own row,
 * adopted as the created row (`adoptCreatedRow`) the moment the server
 * answers, so every buffered update's settlement lands on the row the
 * caller holds; `persisted` then wires it as the caller wires a row it
 * built (`raw`: the row's wire state — the caller's index wiring).
 */
export function pendingRowOf<T extends string>(
  type: T,
  deps: SheetRowHandleDeps,
  registry: RowLocalId.Registry,
  persisted: (row: SheetRow.Handle, raw: Network.SheetRowDb) => void,
  /** The client's row-height preferences: a height kept under the draft's
   *  key follows the created row (`adoptCreatedRow`). `null`: none kept. */
  heights: Option<Pick<RowHeightPreferences, "migrateKeys">> = null,
): SheetRow.Pending<T> {
  const localId = registry.resolve(RowKey.pending(null));
  const inner = SheetRow.from(localId, { type, ownerId: UserId.LOCAL, timeScale: "month" }, null);
  const draft = handleModule.standaloneFacadeOf(inner, deps, FACADE_PORT);
  handleModule.wireSheetRowHandle(draft, { onRowHandleInit: null, resolveParent: null });
  return pendingOf(draft, type, (created) => {
    adoptCreatedRow(inner, registry, created, heights);
    persisted(draft, inner.raw);
  });
}

/**
 * A promoted draft's row becomes the created row, in place: its identity
 * takes the created row's key (`RowLocalId.Registry.rekey`, the promote
 * re-key — the same `RowLocalId`, so every store keyed by it stays valid,
 * and the row stops being pending), and its wire state takes what only the
 * server knows (`id`, `ownerId`, and `metadata` when the draft holds none).
 * The fields the draft writes (`label`, `icon`, `attributes`, …) keep the
 * draft's value — the POST's answer predates the updates buffered while it
 * was in flight, and those settle on this row.
 *
 * A row height kept for the draft (`heights`, keyed by wire key) moves to
 * the created key by the same `RowHeightPreferences.migrateKeys` the
 * copy-on-write sheet promote uses: height follows the row
 * (`sheets/.docs/content/row-subsumption.md`, rule 16).
 */
function adoptCreatedRow(
  inner: SheetRow,
  registry: RowLocalId.Registry,
  created: Network.CreateRowResponse,
  heights: Option<Pick<RowHeightPreferences, "migrateKeys">>,
): void {
  const draftKey = inner.id.toString();
  const rekeyed = registry.rekey(draftKey, created.id.toString());
  if (rekeyed !== inner.id) {
    // The draft's key is no longer in the scope it was interned in (the
    // scope's user changed while it was drafted): the row keeps its pending
    // identity, and its updates still reach the created row (`persistedAs`).
    Tracing.warn("net")("sheet-row/promote-rekey-missed", { draftKey, rowKey: created.id.toString() });
  } else {
    heights?.migrateKeys(new Map([[draftKey, inner.id.toString()]]));
  }
  const raw = inner.raw;
  raw.id = created.id;
  raw.ownerId = created.ownerId;
  if (raw.metadata == null) raw.metadata = created.metadata ?? null;
}

/** Assigned from `SheetRow.Pending`'s static block (its constructor is private). */
let pendingOf: <T extends string>(
  draft: SheetRow.Handle,
  type: T,
  adopt: (created: Network.CreateRowResponse) => void,
) => SheetRow.Pending<T>;

/** What `SheetRowTree` wires onto a `SheetRow.Joined` it builds (its constructor's `wiring`). */
export type JoinedWiring = {
  /** The row's shared cell handle; `null` for trees without a cell accessor. */
  readonly rowHandle: Option<RowCellCache>;
  /** The sheet's system (header) and footer row ids. */
  readonly systemRowIds: Option<ReadonlySet<RowLocalId>>;
  readonly footerRowIds: Option<ReadonlySet<RowLocalId>>;
};

/**
 * The lifecycle of the sheet a row belongs to, as its facade reads it
 * (`SheetRowContext.lifecycle`). A row held past the sheet's `dispose`
 * refuses its fetching and writing verbs (`cells`, `events`, the writes)
 * with `SheetHandle.DisposedError`.
 *
 * @internal SDK-private; `lib.ts` does not export it.
 */
export interface SheetLifecycle {
  /** Throw `SheetHandle.DisposedError` naming `verb` once the sheet was
   *  disposed. */
  assertLive(verb: string): void;
  /** The owner a `CellHandle` built by `verb` checks and watches. */
  cellOwner(verb: string): CellHandle.Owner;
  /** The owning client's lifecycle: a row's durable writes are watched
   *  under it (`observedDurable`). */
  readonly client: ClientLifecycle;
}

/**
 * The cell writer of a sheet a row facade writes its cells through: the
 * sheet's `CellAccessor` (cache + formula cascade), matched structurally.
 *
 * @internal SDK-private (`SheetRowContext`); `lib.ts` does not export it, and it is
 * not a member of the public `SheetRow` namespace. Its `setDurable` /
 * `patchRowDurable` return the settle promise, not a `Durable<T>` — the
 * `*Durable` contract holds only on the public surface.
 */
export interface JoinedCellWriter {
  /** Checked write; rejects with `CellWriteError`. The mirrored cell
   *  records `actor`. */
  set(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void>;
  /** A durable write handed to the client's debounce executor: the promise
   *  is its settle (NOT a `Durable<T>`), resolving on the verdict and
   *  rejecting with `CellWriteError` on a refusal. */
  setDurable(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    id: Option<string>,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void>;
  /** A durable row PATCH handed to the client's debounce executor: the
   *  promise is its settle, resolving on the verdict and rejecting with
   *  `RowWriteError("update")`. */
  patchRowDurable(
    rowId: RowNetworkKey,
    patch: WirePatch,
  ): Promise<void>;
  /** A timepoint write; the answer's cells record `actor`. */
  setCustom(
    rowId: RowNetworkKey,
    dateRange: DateTime.Range<Utc>,
    data: Json | CellValue,
    replaceCellId: Option<CellId>,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void>;
  deleteCustom(rowId: RowNetworkKey, cellId: CellId, actor: Actor): Promise<void>;
  /** Add a date-keyed cell to a multi-cardinality row (`CellTarget.add`),
   *  the server's answer reconciled with the added cell recording
   *  `actor`; rejects with `CellWriteError`. */
  add(
    rowId: RowNetworkKey,
    date: NaiveDate.Partial,
    data: Json,
    position: Option<Rational>,
    actor: Actor,
  ): Promise<void>;
  ensurePersisted(rowId: RowNetworkKey, position: Option<Rational>): Promise<void>;
  /** The one precondition of every cell write (`CellAccessor.promotedRow`):
   *  await the sheet's COW promote, answer the row's current wire key. */
  promotedRow(rowId: RowNetworkKey): Promise<RowNetworkKey>;
  /** The row's cell cache as the sheet holds it (minted and wired for the
   *  sheet's fetches on first use), by its cell-access key. */
  rowHandle(rowId: RowNetworkKey): RowCellCache;
}

/**
 * **SDK-internal**: what the facades of a sheet's rows read and write
 * through — the sheet's rows-side model, its lifecycle, its cell writer and
 * the client's row-field writes. One per handle over the sheet
 * (`SheetHandle.parse` builds it and registers it: `Sheet.ownFacades`; the
 * newest live one answers a facade's calls), or one per joined tree no
 * handle holds. `mint` builds a row's facade the first time the sheet is
 * asked for it (`Sheet.facadeFor` interns it per (Sheet, RowLocalId)).
 *
 * @internal SDK-private; `lib.ts` does not export it.
 */
export class SheetRowContext implements SheetRowContext.Client, RowFacade.Owner {
  readonly api: TypedApi<Network.Routes>;
  readonly host: SheetHost;
  readonly invalidation: InvalidationSink;
  readonly mirror: RowMirror;
  readonly writes: RowFieldWrites;
  readonly listenerErrors?: ListenerErrorSink;
  /** The rows-side sheet the rows belong to. */
  readonly rowsSheet: Sheet;
  /** The sheet's lifecycle; `null` for a joined tree no sheet owns. */
  readonly lifecycle: Option<SheetLifecycle>;
  /** The sheet's cell writer; `null` for a joined tree built without one. */
  readonly cells: Option<JoinedCellWriter>;
  /** The client's events table (a virtual leaf's event writes route through it). */
  readonly eventsTable: Option<EventsTable>;
  /** Where a non-editable sheet keeps a row height change. */
  readonly rowHeightPrefs: Option<RowHeightPreferences>;
  readonly #config: SheetRowContext.Config;
  /** Rows only the rows-side sheet holds, bridged by the joined tree that placed them (`bridge`). */
  readonly #bridged = new Map<RowLocalId, SheetRow>();

  constructor(config: SheetRowContext.Config) {
    this.#config = config;
    this.api = config.api;
    this.host = config.host;
    this.invalidation = config.invalidation;
    this.mirror = config.mirror;
    this.writes = config.writes;
    if (config.listenerErrors != null) this.listenerErrors = config.listenerErrors;
    this.rowsSheet = config.rowsSheet;
    this.lifecycle = config.lifecycle;
    this.cells = config.cells;
    this.eventsTable = config.eventsTable;
    this.rowHeightPrefs = config.rowHeightPrefs;
  }

  /** Whether the sheet allows edits. */
  editable(): boolean {
    return this.#config.editable();
  }

  /** Where row `id` sits in the sheet's tree (`null`: unknown). */
  positionOf(id: RowLocalId): Option<Rational> {
    return this.#config.positionOf(id);
  }

  /**
   * The facade of the row `r_<parentHex>` of this sheet (a parent-stored
   * virtual row's parent, a renderer row's source), or `null` when the
   * sheet's registry holds no such row.
   */
  resolveParent(parentHex: string): Option<SheetRow.Handle> {
    const id = this.rowsSheet.registry.get(RowKey.of(`r_${parentHex}`));
    if (id == null || this.#config.backing(id) == null) return null;
    return rowFacadeOf(this.rowsSheet, id);
  }

  /** A row only the rows-side sheet holds, as the joined tree bridged it: what its facade is minted over. */
  bridge(row: SheetRow): void {
    this.#bridged.set(row.id, row);
  }

  /** Build the facade of row `id` (`RowFacade.Owner`; `Sheet.facadeFor` interns it). */
  mint(id: RowLocalId): RowFacade {
    const inner = nonnull(
      this.#config.backing(id) ?? this.#bridged.get(id) ?? null,
      `SheetRowContext.mint: row ${id.toString()} is neither registered nor bridged by a joined tree`,
    );
    return handleModule.sheetFacadeOf(inner, this.rowsSheet, this, FACADE_PORT);
  }
}

/** Types that go with `SheetRowContext`. */
export namespace SheetRowContext {
  /** What every row facade writes and announces through (a standalone row's client carries more: `SheetRowHandleDeps`). */
  export interface Client {
    readonly api: TypedApi<Network.Routes>;
    readonly host: SheetHost;
    readonly invalidation: InvalidationSink;
    /** Propagates a row's rename, recovers a promote that may have stored (`RowMirror`). */
    readonly mirror: RowMirror;
    /** The client's row-field ledger and its one transport (`RowFieldWrites`). */
    readonly writes: RowFieldWrites;
    /** Where a throw of one of the row's subscribers goes (the client's
     *  `listenerErrorHandler`); absent for a row no client built, which
     *  rethrows (`ListenerErrors.RETHROW`). */
    readonly listenerErrors?: ListenerErrorSink;
  }

  /** What a context is built with (see `SheetRowContext`). */
  export interface Config extends Client {
    readonly rowsSheet: Sheet;
    readonly lifecycle: Option<SheetLifecycle>;
    readonly cells: Option<JoinedCellWriter>;
    readonly eventsTable: Option<EventsTable>;
    readonly rowHeightPrefs: Option<RowHeightPreferences>;
    /** Whether the sheet allows edits (read at each write). */
    editable(): boolean;
    /** Where row `id` sits in the sheet's tree (`null`: unknown). */
    positionOf(id: RowLocalId): Option<Rational>;
    /** The client's registry row for `id` (`null`: not registered — a row only the rows-side sheet holds). */
    backing(id: RowLocalId): Option<SheetRow>;
  }
}

/**
 * **SDK-internal**: THE facade of row `id` of `sheet` — interned per (Sheet,
 * RowLocalId) by `Sheet.facadeFor`, minted by the sheet's owner
 * (`SheetRowContext`) the first time. Every door onto a row of a sheet — its
 * row list, its joined tree, a formula's walk — answers this object.
 */
export function rowFacadeOf(sheet: Sheet, id: RowLocalId): SheetRow.Handle {
  const facade = sheet.facadeFor(id);
  if (!(facade instanceof SheetRow.Handle)) {
    throw new Error(`rowFacadeOf: the facade of row ${id.toString()} is not a SheetRow.Handle (another owner minted it)`);
  }
  return facade;
}

/**
 * What every facade this module builds reads of it at run time
 * (`RowFacadePort`): whether a facade owner is a sheet's row context, and a
 * backing row's move ledger, through the door its static block assigns.
 */
const FACADE_PORT: handleModule.RowFacadePort = {
  contextOf: (owner) => (owner instanceof SheetRowContext ? owner : null),
  movesInFlight: (row) => movesInFlightOf(row),
};

/**
 * Typed wrapper around Network.SheetRowDb.
 * Subtypes provide type-safe access to row-specific attributes.
 */
export class SheetRow<T extends string = string> {
  readonly id: RowLocalId;
  readonly raw: Network.SheetRowDb & SheetRow.Options;
  #descriptor: RowTypeDescriptor;
  /**
   * The closures a preset-expanded (virtual) row carries beside its
   * attributes — see `Graph.Hooks`. `null` for rows no preset expanded.
   */
  readonly graphHooks: Option<Graph.Hooks>;
  #reconciled: Option<JsonObject> = null;
  #seeds: Option<readonly ChildSpec[]> = null;
  /**
   * The timepoint-cell `move`s / `replace`s in flight on this row (the move
   * ledger, `MoveWrites`), shared by every facade over it; `null` until the
   * first one.
   */
  #movesInFlight: Option<MoveWrites<string, DateTime.Range<Utc>>> = null;

  constructor(
    id: RowLocalId,
    raw: Network.SheetRowDb & SheetRow.Options,
    graphHooks: Option<Graph.Hooks>,
  ) {
    this.id = id;
    this.raw = raw;
    this.#descriptor = RowType.get(raw.type);
    this.graphHooks = graphHooks;
  }

  static {
    replaceDescriptor = (row, descriptor) => {
      // Frozen: a row hands its descriptor out (`row.descriptor`,
      // `client.rowRegistry.peek(id).descriptor`), and a render layer's
      // override is shared by every row of its type.
      row.#descriptor = RowTypeDescriptor.frozen(descriptor);
    };
    movesInFlightOf = (row) =>
      row.#movesInFlight ??= new MoveWrites<string, DateTime.Range<Utc>>({
        name: "sheet-row/cell-moves",
        equals: (a, b) => a.start.mse === b.start.mse && a.end.mse === b.end.mse,
        describe: (cellId) => ({ rowKey: row.id.toString(), cellId }),
      });
  }

  /**
   * The row type's metadata (`RowTypeDescriptor`): resolved from the wire
   * type when the row is built, then through the client's registries and the
   * render layer's descriptor overrides when it is registered. Read-only.
   */
  get descriptor(): RowTypeDescriptor {
    return this.#descriptor;
  }

  /**
   * Returns reconciled attributes: descriptor defaults merged with instance data.
   * Instance values win. Cached after first access.
   * Use this for all attribute reads; use `raw.attributes` only for serialization.
   */
  get reconciledAttributes(): Option<JsonObject> {
    if (this.#reconciled !== null) return this.#reconciled;
    const instance = SheetRow.wireAttributes(this.raw);
    const defaults = this.descriptor.defaultAttributes?.(this.raw);
    this.#reconciled = defaults
      ? RowTypeSpecOps.reconcileAttributes(defaults, instance)
      : instance;
    return this.#reconciled;
  }

  /**
   * The row's child seeds: its descriptor's `childSpecs` when the type
   * declares them, else the JSON seeds its wire `attributes.childSpecs`
   * carries. Cached until `resetCache`.
   */
  get childSpecs(): readonly ChildSpec[] {
    if (this.#seeds != null) return this.#seeds;
    const seeds = this.descriptor.childSpecs?.(this.raw) ??
      RowTypeSpecOps.childSpecsFromAttributes(this.reconciledAttributes);
    this.#seeds = seeds;
    return seeds;
  }

  get label(): Option<string> {
    return this.raw.label ?? null;
  }

  get lookupKey(): Option<string> {
    return this.raw.lookupKey ?? null;
  }

  /** Canonical wire `_type` (`data-renderer`, `timeline[column].points`, …). */
  get wireType(): string {
    return this.raw.type;
  }

  get formula(): Option<string> {
    return this.raw.formula ?? null;
  }

  get ownerId(): UserId {
    return this.raw.ownerId;
  }

  get timescaleSetting(): Network.TimeScaleCustom {
    return this.raw.timeScale;
  }

  get icon(): Option<string> {
    return this.raw.icon ?? null;
  }

  get heightPx(): Option<number> {
    return this.raw.heightPx ?? null;
  }

  /** What the row is (`SheetRow.Kind`; a row that states none is `"stored"`). */
  get kind(): SheetRow.Kind {
    return this.raw.kind ?? "stored";
  }

  /** Whether the row is one the sheet builds for itself (`kind` `"system"`). */
  get isSystemRow(): boolean {
    return this.kind === "system";
  }

  /** Whether the row's legend is hidden (`legend` `"hidden"`). */
  get hideLegend(): boolean {
    return this.raw.legend === "hidden";
  }

  /** Whether a preset expands the row (`kind` `"virtual"` or `"parent-stored"`). */
  get isVirtual(): boolean {
    return this.kind === "virtual" || this.kind === "parent-stored";
  }

  /** Whether the row's editable properties live in its parent (`kind` `"parent-stored"`). */
  get parentStored(): boolean {
    return this.kind === "parent-stored";
  }

  /** Label for display — uses descriptor buildLabel with fallback to raw label, then type. */
  get displayLabel(): string {
    return (
      this.descriptor.buildLabel(this.lookupKey, this.reconciledAttributes) ??
      this.raw.label ??
      this.descriptor.id
    );
  }

  /**
   * What the row allows. `canWriteCells` is derived from the row type and
   * the row: false for a formula row (a `formula` expression, or the
   * `formula` type) and for a row whose cells a client-side provider
   * computes (`RowTypeDescriptor.dataOwner` a `CellProvider`) — the SDK's
   * cell verbs refuse such a row (`CellWriteError`, outcome
   * `unaddressable`), nothing sent.
   */
  capabilities(): Capabilities {
    const canWriteCells = this.formula == null &&
      this.descriptor.id !== "formula" &&
      !CellProvider.is(this.descriptor.dataOwner);
    if (this.isSystemRow) {
      return {
        canReparent: false,
        canDuplicate: false,
        canDelete: false,
        canReorder: false,
        canEditHeight: false,
        canEditTitle: false,
        canWriteCells,
      };
    }
    if (this.isVirtual && this.parentStored) {
      // Its parent's attributes store it: there is no row of its own to
      // delete (`handle.rows.delete` resolves `not-deletable`).
      return {
        canReparent: false,
        canDuplicate: false,
        canDelete: false,
        canReorder: false,
        canEditHeight: false,
        canEditTitle: false,
        canWriteCells,
      };
    }
    return {
      canReparent: true,
      canDuplicate: true,
      canDelete: true,
      canReorder: true,
      canEditHeight: true,
      canEditTitle: true,
      canWriteCells,
    };
  }

  /**
   * The row's attribute bag (type defaults merged under the instance's), as
   * the JSON it is. Narrow a key with the `Json` readers
   * (`Json.string(row.attributes(), "unit")`) or decode the whole bag with
   * `attributesAs`.
   */
  attributes(): Option<JsonObject> {
    return this.reconciledAttributes;
  }

  /**
   * The attribute bag decoded by `decode`: `null` when the row has no bag or
   * `decode` rejects it.
   */
  attributesAs<A>(decode: (attributes: JsonObject) => Option<A>): Option<A> {
    const attributes = this.attributes();
    return attributes == null ? null : decode(attributes);
  }

  get rendersChildren(): boolean {
    return this.descriptor.rendersChildren(this.reconciledAttributes);
  }

  get cellLayoutOptions(): Option<CellLayoutOptions> {
    return this.descriptor.cellLayoutOptions(this.reconciledAttributes);
  }

  layoutSpec(sheetTimescale: Timescale): LayoutSpec {
    return this.descriptor.layoutSpec(
      this.heightPx,
      sheetTimescale,
      this.reconciledAttributes,
      this.raw.type,
    );
  }

  initialScrollTop(sheetTimescale: Timescale): number {
    return this.descriptor.initialScrollTop(
      this.heightPx,
      sheetTimescale,
      this.reconciledAttributes,
    );
  }

  requiresHeaderSecondRow(sheetTimescale: Timescale): boolean {
    return this.descriptor.requiresHeaderSecondRow(
      sheetTimescale,
      this.reconciledAttributes,
    );
  }

  headerSecondRowKind(sheetTimescale: Timescale): Option<HeaderSecondRowKind> {
    return this.descriptor.headerSecondRowKind(
      sheetTimescale,
      this.reconciledAttributes,
    );
  }

  buildLabel(): Option<string> {
    return this.descriptor.buildLabel(this.lookupKey, this.reconciledAttributes);
  }

  buildAriaLabel(column: NaiveDate.Partial, cellValue: Option<CellValue>): Option<string> {
    return this.descriptor.buildAriaLabel(this.label, column, cellValue);
  }

  /** Clear the reconciled attributes cache. Call after mutating raw attributes. */
  resetCache(): void {
    this.#reconciled = null;
    this.#seeds = null;
  }

  is<K extends keyof typeof SheetRow.Types>(
    type: K,
  ): this is SheetRow.ByType<K> {
    return this.raw.type === type;
  }
}

/**
 * Types and values that go with `SheetRow`: `HandleHooks`, `Options`, `Kind`,
 * `Legend`, `CoreFields`, `WireFields`, and 45 more.
 */
export namespace SheetRow {
  /**
   * The wiring hooks `wireSheetRowHandle` installs on a `SheetRow.Handle`.
   * Every field is required; `null` leaves that hook as it was.
   */
  export type HandleHooks = {
    onRowHandleInit: Option<(rh: RowCellCache) => void>;
    resolveParent: Option<(parentHex: string) => Option<Handle>>;
  };

  /**
   * What the wire row does not carry: the row's kind and whether its legend
   * shows. Each is absent unless stated (absent: `"stored"`, `"shown"`). The
   * row's own fields (`label`, `heightPx`, `formula`, `attributes`,
   * `metadata`, `icon`, `lookupKey`) are the wire row's
   * (`Network.SheetRowDb`): always present, `null` when unset.
   */
  export type Options = {
    kind?: Kind;
    legend?: Legend;
  };

  /**
   * What a row is: `"stored"` — a row of its own on the server (the
   * default); `"system"` — a row the sheet builds for itself (the header, the
   * footer, the debug rows), never network data; `"virtual"` — a row a preset
   * expands; `"parent-stored"` — a virtual row whose editable properties
   * (timeScale, heightPx, label) live in its parent's
   * `attributes.nestedRows[preset]`, never a database row of its own (derived
   * UI state that should not persist independently).
   */
  export type Kind = "stored" | "system" | "virtual" | "parent-stored";

  /** Whether a row's legend shows. */
  export type Legend = "shown" | "hidden";

  /** Required row properties — every row has these. */
  export type CoreFields = {
    ownerId: UserId;
    /** The row's wire field (`Network.SheetRowDb.timeScale`). */
    timeScale: Network.TimeScaleCustom;
  };

  /**
   * The row fields as a wire row states them (`Network.SheetRowDb`): every
   * one present, `null` when the row has none. The attribute bag and
   * metadata are the wire's untyped JSON; `wireAttributes` narrows them.
   */
  export type WireFields = {
    label: Option<string>;
    heightPx: Option<number>;
    formula: Option<string>;
    icon: Option<string>;
    lookupKey: Option<string>;
    attributes: Json;
    metadata: Json;
  };

  /**
   * The row fields of an authored row (a pending row, an ephemeral seed, a
   * rows-side row bridged into the joined tree): each absent unless the row
   * sets it, never `null`. An absent field takes the rows-side row's value
   * when the joined tree merges the two (`label`, `lookupKey`, `heightPx`,
   * `attributes`).
   */
  export type AuthoredFields = {
    label?: string;
    heightPx?: number;
    formula?: string;
    icon?: string;
    lookupKey?: string;
    attributes?: JsonObject;
    metadata?: JsonObject;
  };

  /**
   * Structural row metadata accepted by SheetRow.Registry.set and
   * SheetHandle.parse: a row's type, owner and timescale, its flags, and its
   * fields in one of the two shapes a row arrives in — a wire row's
   * (`WireFields`: every field stated; `Network.SheetRowDb` satisfies it, so
   * the boundary needs no cast) or an authored row's (`AuthoredFields`:
   * absent unless set).
   */
  export type Input = Options & CoreFields & { type: string } & (WireFields | AuthoredFields);

  /** Structural interface satisfied by SheetRow — the row-intrinsic fields. */
  export interface Like<Id = RowLocalId> {
    readonly id: Id;
    readonly descriptor: RowTypeDescriptor;
    readonly label: Option<string>;
    readonly timescaleSetting: Timescale | "custom";
    readonly isSystemRow: boolean;
    readonly rendersChildren: boolean;
    readonly cellLayoutOptions: Option<CellLayoutOptions>;
    layoutSpec(sheetTimescale: Timescale): LayoutSpec;
    initialScrollTop(sheetTimescale: Timescale): number;
  }

  export class HeaderRow extends SheetRow<"header"> {}
  export class TextRow extends SheetRow<"text"> {}
  export class NumberRow extends SheetRow<"number"> {}
  export class GroupRow extends SheetRow<"group"> {}
  export class FormulaRow extends SheetRow<"formula"> {}
  export class CheckmarkRow extends SheetRow<"checkmark"> {}
  export class GraphRow extends SheetRow<"graph"> {}
  export class CalendarRow extends SheetRow<"calendar"> {}
  export class CalendarListRow extends SheetRow<"calendar-list"> {}
  export class CalendarMonthRow extends SheetRow<"calendar-month"> {}
  export class WeatherRow extends SheetRow<"weather"> {}
  export class TimezoneRow extends SheetRow<"timezone"> {}
  export class BlockRow extends SheetRow<"block"> {}
  export class LogsRow extends SheetRow<"logs"> {}
  export class TimelineRow extends SheetRow<"timeline"> {}
  export class StickerRow extends SheetRow<"sticker"> {}
  export class DebugRow extends SheetRow<"debug"> {}
  export class TaskRow extends SheetRow<"task"> {}
  export class JournalRow extends SheetRow<"journal"> {}
  export class DataRow extends SheetRow<"data"> {}
  export class DataCanvasRow extends SheetRow<"data-canvas"> {}
  export class DataSeriesRow extends SheetRow<"data-series"> {}
  export class DataRendererRow extends SheetRow<"data-renderer"> {}

  export const Types = {
    header: HeaderRow,
    text: TextRow,
    number: NumberRow,
    group: GroupRow,
    formula: FormulaRow,
    checkmark: CheckmarkRow,
    graph: GraphRow,
    calendar: CalendarRow,
    "calendar-list": CalendarListRow,
    timeline: TimelineRow,
    "calendar-month": CalendarMonthRow,
    weather: WeatherRow,
    timezone: TimezoneRow,
    block: BlockRow,
    logs: LogsRow,
    sticker: StickerRow,
    debug: DebugRow,
    task: TaskRow,
    journal: JournalRow,
    data: DataRow,
    "data-canvas": DataCanvasRow,
    "data-series": DataSeriesRow,
    "data-renderer": DataRendererRow,
  } as const;

  export type ByType<K extends keyof typeof Types> = InstanceType<
    (typeof Types)[K]
  >;

  /**
   * The row of a wire type `Types` does not name (a plugin's own row type, a
   * type newer than this SDK): the generic row, under its own name, so a
   * reader can tell it from a built-in one (`row instanceof SheetRow.Unknown`).
   */
  export class Unknown extends SheetRow {}

  /** The class a row of wire `type` is: its built-in class, or `Unknown`. */
  export function classFor(type: string): (typeof Types)[keyof typeof Types] | typeof Unknown {
    return isBuiltinType(type) ? Types[type] : Unknown;
  }

  /** Whether `type` is a key of `Types` (its own, not an inherited one). */
  function isBuiltinType(type: string): type is keyof typeof Types {
    return Object.hasOwn(Types, type);
  }

  export function from(
    id: RowLocalId,
    raw: SheetRow.Input,
    graphHooks: Option<Graph.Hooks>,
  ): SheetRow {
    const Ctor = classFor(raw.type);
    return new Ctor(id, raw as Network.SheetRowDb & SheetRow.Options, graphHooks);
  }

  /**
   * A row's wire attribute bag as JSON: the network row types it loosely,
   * so this is the one place it is narrowed.
   */
  export function wireAttributes(raw: Network.SheetRowDb): Option<JsonObject> {
    return Json.object(raw.attributes);
  }

  export class Registry {
    readonly #db = new Map<RowLocalId, SheetRow>();
    readonly descriptors = new DescriptorOverrides();
    /** The owning client's registries (`ClientConfig.registries`): a registered row's descriptor resolves through them. */
    readonly #registries: SheetRegistries;
    /** Wire types already warned unregistered (`RowType.warnUnregistered`). */
    readonly #warnedUnregistered = new Set<string>();

    /** `registries`: the owning client's (`null`: the built-ins, for a standalone registry). */
    constructor(registries: Option<SheetRegistries>) {
      this.#registries = registries ?? SheetRegistries.BUILTINS;
    }

    /**
     * Register a row. `graphHooks` are a preset-expanded (virtual) row's
     * closures (`ChildSpec.graph`); `null` for every other row.
     */
    set(
      id: RowLocalId,
      raw: SheetRow.Input,
      graphHooks: Option<Graph.Hooks>,
    ): void {

      const row = SheetRow.from(id, raw, graphHooks);
      RowType.warnUnregistered(raw.type, this.#warnedUnregistered);
      replaceDescriptor(row, RowType.inRegistries(row.descriptor, this.#registries));
      this.descriptors.apply(row);
      this.#db.set(id, row);
    }

    /**
     * SDK-internal effective metadata for a wire row; never mutates its raw
     * fields. `key` is the row's key as the caller holds it: a row built from
     * authored input (`SheetRow.from`) has no wire id.
     */
    effectiveMetadata(raw: Network.SheetRowDb, key: string): Network.SheetRowDb {
      const attributes = Json.object(raw.attributes);
      const metadata = RowTypeSpecOps.reconcileMetadataForWireType(
        raw.type,
        {
          id: key,
          type: raw.type,
          ...(raw.label == null ? {} : { label: raw.label }),
          ...(raw.lookupKey == null ? {} : { lookupKey: raw.lookupKey }),
          ...(attributes == null ? {} : { attributes }),
        },
        Json.object(raw.metadata),
        this.#registries,
      );
      return { ...raw, metadata };
    }

    get(id: RowLocalId): Option<SheetRow> {
      return this.#db.get(id) ?? null;
    }

    raw(id: RowLocalId): Option<Network.SheetRowDb> {
      return this.#db.get(id)?.raw ?? null;
    }

    iter(): MapIterator<[RowLocalId, SheetRow<string>]> {
      return this.#db.entries();
    }

    /**
     * Drop `ids`' rows: their owner (the client, once the last open sheet
     * holding them closed) no longer reads them. An id with no row is
     * skipped. Returns how many rows left.
     */
    release(ids: Iterable<RowLocalId>): number {
      let released = 0;
      for (const id of ids) if (this.#db.delete(id)) released++;
      return released;
    }
  }

  export class DescriptorOverrides {
    readonly #overrides = new Map<string, RowTypeDescriptor.Override>();

    register(type: string, override: RowTypeDescriptor.Override): void {
      this.#overrides.set(type, override);
    }

    registerAll(overrides: Iterable<[string, RowTypeDescriptor.Override]>): void {
      for (const [type, override] of overrides) {
        this.register(type, override);
      }
    }

    apply(row: SheetRow): void {
      // Overrides register under render-plugin lookup ids (plugin id +
      // aliases); rows resolve by WIRE type. Descriptor ids are shared
      // across row families (e.g. the `timeline` descriptor backs both
      // `timeline[row]` and `timeline[column]` rows), so keying off
      // `descriptor.id` would cross-apply an override to siblings —
      // and historically matched nothing (see 256b295).
      const override = this.#overrides.get(row.raw.type);
      if (!override) return;
      const base = row.descriptor;
      const { layoutSpec, cellLayoutOptions, buildLabel } = override;
      replaceDescriptor(row, {
        ...base,
        ...(layoutSpec && {
          layoutSpec: (_hp: Option<number>, ts: Timescale) => layoutSpec(row, ts),
        }),
        ...(cellLayoutOptions && {
          cellLayoutOptions: () => cellLayoutOptions(row),
        }),
        ...(buildLabel && {
          buildLabel: () => buildLabel(row),
        }),
      });
    }

    applyAll(registry: SheetRow.Registry): void {
      for (const [_, row] of registry.iter()) this.apply(row);
    }
  }

  // ── Cells of a held row ─────────────────────────────────────────

  /**
   * One entry of a row's cell read (`row.cells(range)`, `row.single()?.peek(day)`),
   * carrying its address: a `kind: "date"` entry is a date-keyed cell (its
   * `date`), a `kind: "timepoint"` entry a timepoint cell (its `dtr`). Each
   * carries the row that read it (`row`): pass it to `CellTarget.cell` to
   * write it through that row — another row's verbs reject it. Read-only.
   */
  export type Cell = CellTarget.Held;

  /** A date-keyed cell as a row's read hands it out (see `Cell`): the
   *  model tier's `CellEntry` (its column `date`, its `id` on a
   *  multi-cardinality row, else `null`) and the row that read it. `Id`
   *  narrows the id: the single facade's reads hand out `DateCell<null>`
   *  (the column's one cell, which has no id). */
  export type DateCell<Id extends Option<CellId> = Option<CellId>> = CellTarget.HeldDateCell<Id>;

  /** A timepoint cell as a row's read hands it out (see `Cell`): the model
   *  tier's `CellEntry.Timepoint` (its `id`, its range `dtr`) and the row
   *  that read it. */
  export type TimepointCell = CellTarget.HeldTimepointCell;

  /**
   * The cell verbs both row facades (`SheetRow.Handle`,
   * `SheetRow.Joined.Handle`) share: one read, and the narrowed facades
   * that hold every cell write. A row has no write verb of its own: the
   * writes are the facade the row hands out for its cardinality —
   * `writableSingle()` (one cell per column) or `writableMulti()` (cells
   * told apart by id) — each `null` on a row of the other cardinality and
   * on a row whose cells are computed (`capabilities().canWriteCells`
   * false), so a write to a computed row or a cardinality mismatch does
   * not compile. Every write takes a `CellTarget` and the writing `actor`;
   * the row routes it to the store its address names.
   */
  export interface Cells {
    /** The row's id — the row a cell known by id is addressed on
     *  (`CellTarget.keyed(row.id, …)`, `CellTarget.timepoint(row.id, …)`). */
    readonly id: RowLocalId;
    /** Every cell over `range`, each carrying its address. */
    cells(range: NaiveDate.Range | DateTime.Range<Utc>): RowRangeCellHandle<Cell>;
    /** This row's one-cell reads on a single-cardinality row (one cell per
     *  column: `get` / `peek` / `cell` of a day), its cells computed or
     *  not; `null` on a multi-cardinality row. Read-only: the writes are
     *  `writableSingle()`'s. */
    single(): Option<SingleReads>;
    /** This row's cell verbs narrowed to a single-cardinality row whose
     *  cells take writes (`CellTarget.date`); `null` on a multi-cardinality
     *  row, and on a row whose cells are computed
     *  (`capabilities().canWriteCells` false: a formula row, a client-side
     *  provider's row). */
    writableSingle(): Option<SingleCells>;
    /** This row's cell verbs narrowed to a multi-cardinality row whose cells
     *  take writes (cells told apart by id: `CellTarget.add`); `null` on a
     *  single-cardinality row, and on a row whose cells are computed. */
    writableMulti(): Option<MultiCells>;
  }

  /**
   * The one-cell reads of a single-cardinality row — one cell per column —
   * as `row.single()` hands them out, on every such row (a computed one's
   * cells are read like a stored one's). Read-only: a row whose cells take
   * writes hands its writes out as `row.writableSingle()` (`SingleCells`,
   * these reads and the writes).
   */
  export interface SingleReads {
    readonly cardinality: "single";
    /**
     * The cell of `day`'s column — a `NaiveDate` names its day, a
     * `NaiveDate.Partial` its column (a week or a month on a row of that
     * scale) — cached, else fetched for this row; `null` when the column is
     * empty. Rejects with the read's `SdkRequestError` (and with
     * `SheetHandle.DisposedError` / `LonaSdk.Client.DisposedError` once the
     * row's owner is disposed); outcome `unaddressable` when the row turned
     * out to hold several cells per column (see `peek`).
     */
    get(day: NaiveDate | NaiveDate.Partial): Promise<Option<DateCell<null>>>;
    /**
     * The cached cell of `day`'s column, or `null` when the column is empty;
     * never fetches. A row whose metadata showed it multi-cardinality after
     * this facade was handed out has no one cell per column: the read throws
     * `SdkRequestError` (step `get-cells`, outcome `unaddressable`) rather
     * than answer an empty column — read it with `row.cells(range)`.
     */
    peek(day: NaiveDate | NaiveDate.Partial): Option<DateCell<null>>;
    /**
     * The progressive read of `day`'s column (`CellHandle`: `onLoading` /
     * `onData` / `onDataFull` / `onError`), each fire delivering the column's
     * `DateCell` — pass it to `CellTarget.cell` to write it back through this
     * row. Owned by the row's sheet (or a standalone row's client): held past
     * its `dispose` it throws its `DisposedError`.
     */
    cell(day: NaiveDate | NaiveDate.Partial): CellHandle;
  }

  /**
   * The cell verbs of a single-cardinality row whose cells take writes —
   * one cell per column — as `row.writableSingle()` hands them out: the
   * one-cell reads (`SingleReads`) and the writes. A date-keyed write takes
   * `CellTarget.Dated` (the column's cell, `CellTarget.date(day)`, or a held
   * id-less date-keyed cell); the row's timepoint cells (a sticker row's)
   * are written with `CellTarget.range` or a held timepoint cell, and moved
   * with `moveCell` / `replaceCell`.
   */
  export interface SingleCells extends SingleReads {
    /** Checked write of the column's cell, a new timepoint cell (`range`) or
     *  a held timepoint cell; rejects with `CellWriteError` (and with
     *  nothing else once the server accepted it). */
    write(target: CellTarget.Single, data: Json, actor: Actor): Promise<void>;
    /** Durable write of the column's cell, through the client's debounce
     *  executor (a timepoint address has no durable form). */
    writeDurable(target: CellTarget.Dated, data: Json, actor: Actor): Durable<void>;
    /** Checked clear of the column's cell or a held timepoint cell; rejects
     *  with `CellWriteError`. */
    clear(target: CellTarget.Dated | CellTarget.OnTimepointCell, actor: Actor): Promise<void>;
    /** Durable clear of the column's cell. */
    clearDurable(target: CellTarget.Dated, actor: Actor): Durable<void>;
    /** Move a held timepoint cell to `to`, painted first and taken back on a
     *  refusal (`CellWriteError("write-timepoint-cell")`). */
    moveCell(target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, actor: Actor): Promise<void>;
    /** Replace a held timepoint cell with `data` at `to` in one write,
     *  painted first and taken back on a refusal. */
    replaceCell(target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, data: Json, actor: Actor): Promise<void>;
  }

  /**
   * The cell verbs of a multi-cardinality row whose cells take writes — its
   * cells told apart by id (tasks, events, calendar lists, scheduling
   * links) — as `row.writableMulti()` hands them out (a multi-cardinality
   * row has no one-cell reads: read it with `row.cells(range)`). `write`
   * also takes `CellTarget.add(day)`, a new date-keyed cell whose id the
   * server mints, and `CellTarget.addWithId(day, id)`, a new one whose id
   * the caller chooses (only a multi-cardinality row's cells have ids); a
   * column names none of the row's cells, so no verb takes
   * `CellTarget.date`.
   */
  export interface MultiCells {
    readonly cardinality: "multi";
    /** Checked write of a new date-keyed cell (`add`, `addWithId`), a held
     *  cell or a new timepoint cell (`range`); rejects with `CellWriteError`. */
    write(target: CellTarget.Multi, data: Json, actor: Actor): Promise<void>;
    /** Durable write of a held date-keyed cell, or of a new one whose id the
     *  caller chooses (`addWithId`). */
    writeDurable(target: CellTarget.MultiDated, data: Json, actor: Actor): Durable<void>;
    /** Checked clear of a held cell. */
    clear(target: CellTarget.OnCell, actor: Actor): Promise<void>;
    /** Durable clear of a held date-keyed cell. */
    clearDurable(target: CellTarget.OnDatedCell, actor: Actor): Durable<void>;
    /** Move a held timepoint cell to `to`, painted first and taken back on a
     *  refusal (`CellWriteError("write-timepoint-cell")`). */
    moveCell(target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, actor: Actor): Promise<void>;
    /** Replace a held timepoint cell with `data` at `to` in one write,
     *  painted first and taken back on a refusal. */
    replaceCell(target: CellTarget.OnTimepointCell, to: DateTime.Range<Utc>, data: Json, actor: Actor): Promise<void>;
  }

  /**
   * The verbs of a row whose model is an alias row, as
   * `SheetRow.Joined.Handle.alias()` hands them out.
   */
  export interface AliasCells {
    /**
     * Set the alias prop `name` to `value`, CHECKED: the prop paints at once
     * (a bound child re-expands), announced as `actor`, is mirrored into the
     * row's persisted `attributes.props` and persisted. The PATCH replaces
     * `attributes` whole, so each write sends the whole bag the row shows —
     * its confirmed bag with this write — and the row's prop writes settle
     * at row scope: an accepted write's bag becomes the confirmed state and
     * retires every older write, and a refusal takes back only what no
     * accepted bag carried (announced as `SheetRow.Handle.ACTOR`) and
     * rejects with `RowWriteError` (step `"update"`), its details saying
     * what became of the value. Held past the owning sheet's `dispose`, it
     * rejects with `SheetHandle.DisposedError`, painting and sending nothing.
     */
    setProp(name: string, value: Json, actor: Actor): Promise<void>;
  }

  // ── SheetRow.Handle ─────────────────────────────────────────────

  // The row facade is declared in `sheet-row-handle.ts`; it is a member of
  // this namespace, published as `SheetRow.Handle`.
  export import Handle = handleModule.Handle;

  // ── SheetRow.Pending ────────────────────────────────────────────

  /**
   * A pending row (`client.rows.pending(type)`): a local draft of wire type
   * `T` that a create-row form fills in before it exists. It reads as a row
   * does (`label`, `attributes`, `descriptor`, …), buffers `update` /
   * `updateDurable` (applied at once; they settle once the server holds
   * them), and persists with `promote`, which resolves the server-backed
   * `SheetRow.Handle` of the created row — the draft's own row, so it shows
   * every buffered update as it settles. It is not a `SheetRow.Handle`: a
   * draft has no cells to read or write, and no held row has a `promote`.
   *
   * ```ts
   * const draft = client.rows.pending("text");
   * await draft.update({ label: "Groceries" }, actor);
   * const row = await draft.promote(sheetId, position, actor);
   * ```
   */
  export class Pending<T extends string = string> {
    static {
      pendingOf = (draft, type, adopt) => new Pending(draft, type, adopt);
    }

    /** The held draft row the reads and buffered updates go through — and, once promoted, the created row. */
    readonly #draft: Handle;
    readonly #type: T;
    /** Makes the draft's row the created one from the server's answer (`adoptCreatedRow`). */
    readonly #adopt: (created: Network.CreateRowResponse) => void;

    private constructor(draft: Handle, type: T, adopt: (created: Network.CreateRowResponse) => void) {
      this.#draft = draft;
      this.#type = type;
      this.#adopt = adopt;
    }

    /**
     * The draft's identity: a pending `RowLocalId` (`id.isPending()`) until
     * `promote` succeeded, then the same object keyed by the created row.
     */
    get id(): RowLocalId {
      return this.#draft.id;
    }
    /** The wire type the draft was made with. */
    get wireType(): T {
      return this.#type;
    }
    /** `true` until `promote` succeeded. */
    get isPending(): boolean {
      return this.#draft.isPending && !this.#promoted;
    }
    #promoted = false;
    get label(): Option<string> {
      return this.#draft.label;
    }
    get formula(): Option<string> {
      return this.#draft.formula;
    }
    get lookupKey(): Option<string> {
      return this.#draft.lookupKey;
    }
    get timescaleSetting(): Network.TimeScaleCustom {
      return this.#draft.timescaleSetting;
    }
    get attributes(): Option<JsonObject> {
      return this.#draft.attributes();
    }
    /** The draft's attributes decoded by `decode` (`null` when it has none). */
    attributesAs<A>(decode: (attributes: JsonObject) => Option<A>): Option<A> {
      return this.#draft.attributesAs(decode);
    }
    get icon(): Option<string> {
      return this.#draft.icon;
    }
    get heightPx(): Option<number> {
      return this.#draft.heightPx;
    }
    get descriptor(): RowTypeDescriptor {
      return this.#draft.descriptor;
    }

    /**
     * Update the draft, CHECKED: applied at once, and settled once the
     * server holds the change — carried by `promote`'s POST, or (a change
     * buffered after the POST was sent) by a PATCH of the created row. It
     * rejects with the promote's (or that PATCH's) `RowWriteError`, the
     * change taken back first. See `SheetRow.Handle.update`.
     */
    update(patch: RowPatch, actor: Actor): Promise<void> {
      return this.#draft.update(patch, actor);
    }

    /** `update` handed off without waiting: see `SheetRow.Handle.updateDurable`. */
    updateDurable(patch: RowPatch, actor: Actor): Durable<void> {
      return this.#draft.updateDurable(patch, actor);
    }

    /**
     * Persist the draft into sheet `sheetId` at `position`
     * (`POST /api/sheets/{sheetKey}/row`) and resolve the server-backed
     * `SheetRow.Handle` of the created row — write through it from then on.
     * It is the draft's own row, re-keyed to the created row: it shows every
     * update buffered on the draft, and each one's settlement (a refused
     * follow-up taken back on it too) — so reading the returned row or the
     * draft answers the same. The updates buffered before the POST was sent
     * resolve with it; one buffered while it is in flight is sent to the
     * created row afterwards.
     * Throws `RowWriteError("add")` when the server refuses or does not
     * answer — the buffered updates waiting on it are taken back and reject
     * with it, and the draft stays pending, so a retry carries none of
     * them. An unanswered promote may have created the row: every open
     * handle of sheet `sheetId` re-reads its row list and lists the row the
     * server holds (see Known gaps: the POST carries no idempotency key, so
     * the draft stays pending and a retry may create a second row). Once
     * promoted, a second `promote` sends nothing and
     * throws `RowWriteError("add")` with outcome `"unaddressable"`. `actor`
     * stamps the `RowDataChanged` (field `"pending"`) a landed promote
     * emits.
     */
    async promote(sheetId: SheetId, position: Rational, actor: Actor): Promise<Handle> {
      if (this.#promoted) {
        throw new RowWriteError({
          step: "add",
          method: "POST",
          path: "/api/sheets/{sheetKey}/row",
          failure: { outcome: "unaddressable" },
          details: `row.promote: the draft ${this.#draft.id.toString()} was already promoted`,
        });
      }
      await handleModule.promoteDraft(this.#draft, sheetId, position, actor, this.#adopt);
      this.#promoted = true;
      return this.#draft;
    }
  }

  // ── SheetRow.Joined ─────────────────────────────────────────────

  /**
   * Row joined to a sheet — has parent reference, position, children, graph deps.
   * Access via SheetHandle.joinedTree().get(id) or SheetRowTree.get(id).
   */
  export class Joined<T extends string = string> {
    #graph: Option<DependencyGraph<RowLocalId>> = null;
    readonly #sharedHandle: Option<RowCellCache>;
    readonly #systemRowIds: Option<ReadonlySet<RowLocalId>>;
    readonly #footerRowIds: Option<ReadonlySet<RowLocalId>>;

    /** The row's shared cell handle; `null` for trees without a cell accessor. */
    get rowHandle(): Option<RowCellCache> {
      return this.#sharedHandle;
    }

    /**
     * The node of `row` in a joined tree: its edge (`parent`, `position`,
     * `children`) and what the tree wired onto it (`JoinedWiring`: the row's
     * shared cell cache, the sheet's system and footer rows). The row's
     * facade (`SheetRow.Handle`) reads its tree position from the node of
     * the tree that placed it last.
     */
    constructor(
      readonly row: SheetRow<T>,
      readonly edge: SheetRowEdge,
      readonly registry: RowLocalId.Registry,
      wiring: JoinedWiring,
    ) {
      this.#sharedHandle = wiring.rowHandle;
      this.#systemRowIds = wiring.systemRowIds;
      this.#footerRowIds = wiring.footerRowIds;
    }

    /** Bind the dependency graph the dependency reads below consult (no caller wires one today). */
    #setGraph(graph: DependencyGraph<RowLocalId>): void {
      this.#graph = graph;
    }

    /** Row IDs this row depends on (forward edges). */
    get dependsOn(): Set<RowLocalId> {
      return this.#graph?.getDependencies(this.id) ?? new Set();
    }

    /** Row IDs that depend on this row (reverse edges). */
    get dependents(): Set<RowLocalId> {
      return this.#graph?.getDirectDependents(this.id) ?? new Set();
    }

    // ── Delegated from SheetRow ──────────────────────────────────

    get id(): RowLocalId {
      return this.row.id;
    }
    get label(): Option<string> {
      return this.row.label;
    }
    get lookupKey(): Option<string> {
      return this.row.lookupKey;
    }
    /** Canonical wire `_type` — see `SheetRow.wireType`. */
    get wireType(): string {
      return this.row.wireType;
    }
    get formula(): Option<string> {
      return this.row.formula;
    }
    get icon(): Option<string> {
      return this.row.icon;
    }
    get heightPx(): Option<number> {
      return this.row.heightPx;
    }
    get ownerId(): UserId {
      return this.row.ownerId;
    }
    get timescaleSetting(): Timescale | "custom" {
      return this.row.timescaleSetting;
    }
    get isVirtual(): boolean {
      return this.row.isVirtual;
    }
    get parentStored(): boolean {
      return this.row.parentStored;
    }
    get hideLegend(): boolean {
      return this.row.hideLegend;
    }
    get displayLabel(): string {
      return this.row.displayLabel;
    }
    get rendersChildren(): boolean {
      return this.row.rendersChildren;
    }
    get cellLayoutOptions(): Option<CellLayoutOptions> {
      return this.row.cellLayoutOptions;
    }
    get descriptor(): RowTypeDescriptor {
      return this.row.descriptor;
    }
    capabilities(): Capabilities {
      return this.row.capabilities();
    }
    /** The row's attribute bag — see `SheetRow.attributes`. */
    attributes(): Option<JsonObject> {
      return this.row.attributes();
    }
    /** The attribute bag decoded by `decode` — see `SheetRow.attributesAs`. */
    attributesAs<A>(decode: (attributes: JsonObject) => Option<A>): Option<A> {
      return this.row.attributesAs(decode);
    }
    /** The row's child seeds — see `SheetRow.childSpecs`. */
    get childSpecs(): readonly ChildSpec[] {
      return this.row.childSpecs;
    }
    /** A preset-expanded row's closures — see `SheetRow.graphHooks`. */
    get graphHooks(): Option<Graph.Hooks> {
      return this.row.graphHooks;
    }
    /** Set by SheetRowTree when the row is subsumed by its parent's active layout. */
    subsumed: boolean = false;
    layoutSpec(sheetTimescale: Timescale): LayoutSpec {
      return this.row.layoutSpec(sheetTimescale);
    }
    initialScrollTop(sheetTimescale: Timescale): number {
      return this.row.initialScrollTop(sheetTimescale);
    }
    requiresHeaderSecondRow(sheetTimescale: Timescale): boolean {
      return this.row.requiresHeaderSecondRow(sheetTimescale);
    }
    headerSecondRowKind(sheetTimescale: Timescale): Option<HeaderSecondRowKind> {
      return this.row.headerSecondRowKind(sheetTimescale);
    }
    buildLabel(): Option<string> {
      return this.row.buildLabel();
    }
    buildAriaLabel(column: NaiveDate.Partial, cellValue: Option<CellValue>): Option<string> {
      return this.row.buildAriaLabel(column, cellValue);
    }

    // ── Tree metadata (from sheet context) ────────────────────────

    get isSystemRow(): boolean {
      return this.#systemRowIds?.has(this.row.id) ?? false;
    }
    get isFooterRow(): boolean {
      return this.#footerRowIds?.has(this.row.id) ?? false;
    }

    // ── Tree navigation ───────────────────────────────────────────

    get position(): Rational {
      return this.edge.position;
    }
    children(): Joined.Handle[] {
      return this.edge.children();
    }
    childrenRows(): Joined[] {
      return this.edge.children().map((c) => c.joined);
    }
    childrenIds(): RowLocalId[] {
      return this.edge.childrenIds();
    }
    parent(): Option<Joined.Handle> {
      return this.edge.parent();
    }

    /** Walk up the ancestor chain and return the first match. */
    findAncestor<T extends ModelRow>(
      variantClass: new (...args: never[]) => T,
    ): Option<T> {
      let current: Option<Joined.Handle> = this.parent();
      while (current) {
        const face = current.variant;
        const v = face == null ? null : liveRowOf(face);
        if (v != null && v instanceof variantClass) return v as T;
        current = current.parent();
      }
      return null;
    }
  }

  export namespace Joined {
    /**
     * A row of a joined tree: the row's ONE facade (`SheetRow.Handle`), its
     * tree reads answered by the tree that placed it last — the same object
     * the sheet's row list reaches (`Sheet.facadeFor` interns it per
     * (Sheet, RowLocalId)).
     */
    export type Handle = SheetRow.Handle;
  }
}